import React, { useState, useMemo, useEffect } from "react";
import { Card, Table, Row, Col, Form, InputGroup, Button, Pagination, Dropdown, Badge, Modal, Alert, Spinner, Toast, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { fetchWebhooksList, createWebhook, updateWebhook, deleteWebhook, testWebhook } from "../services/api";

// ============================================
// CONSTANTES
// ============================================
const evenementsDisponibles = ["solde_bas", "statut_sms"];
const statutsDisponibles = ["actif", "inactif"];

// ============================================
// UTILITAIRES
// ============================================
const getNotificationIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle-fill';
    case 'error': return 'bi-exclamation-circle-fill';
    case 'warning': return 'bi-exclamation-triangle-fill';
    case 'info': return 'bi-info-circle-fill';
    default: return 'bi-bell-fill';
  }
};

const getNotificationBg = (type) => {
  switch (type) {
    case 'success': return 'success';
    case 'error': return 'danger';
    case 'warning': return 'warning';
    case 'info': return 'info';
    default: return 'primary';
  }
};

const getAlertIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle';
    case 'danger': return 'bi-exclamation-triangle';
    case 'warning': return 'bi-exclamation-triangle';
    case 'info': return 'bi-info-circle';
    default: return 'bi-bell';
  }
};

export default function WebhooksManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedWebhooks, setSelectedWebhooks] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [selectedStatutFilter, setSelectedStatutFilter] = useState('all');
  const [testPayload, setTestPayload] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  const [webhookForm, setWebhookForm] = useState({
    url: '',
    event: '',
    status: 'actif'
  });

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [{ id, message, type, show: true }, ...prev]);
    setTimeout(() => hideNotification(id), duration);
  };

  const hideNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) setTimeout(() => hideAlert(), duration);
  };

  const hideAlert = () => {
    setAlertMessage({ show: false, message: '', type: 'info' });
  };

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async (showRefreshSpinner = false) => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem('authToken');

      if (!token) {
        setError("Token d'authentification non trouvé");
        showAlert("Token d'authentification non trouvé. Veuillez vous reconnecter.", 'danger');
        return;
      }

      const response = await fetchWebhooksList(token);

      if (response && response.data) {
        setWebhooks(response.data);
        if (showRefreshSpinner) {
          showNotification('Liste des webhooks actualisée', 'success', 3000);
        }
      } else {
        setWebhooks([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des webhooks:', error);
      const errorMessage = error.message || "Erreur lors du chargement des webhooks";
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = webhooks.filter(webhook => {
      const matchesSearch =
        webhook.url.toLowerCase().includes(filterText.toLowerCase()) ||
        webhook.event.toLowerCase().includes(filterText.toLowerCase());
      const matchesStatut = selectedStatutFilter === 'all' || webhook.status === selectedStatutFilter;
      return matchesSearch && matchesStatut;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [filterText, sortField, sortDirection, selectedStatutFilter, webhooks]);

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = webhooks.length;
    const actifs = webhooks.filter(w => w.status === "actif").length;
    const inactifs = webhooks.filter(w => w.status === "inactif").length;
    return { total, actifs, inactifs };
  }, [webhooks]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedWebhooks(currentItems.map(w => w.id));
    else setSelectedWebhooks([]);
  };

  const handleSelectWebhook = (webhookId) => {
    setSelectedWebhooks(prev =>
      prev.includes(webhookId)
        ? prev.filter(id => id !== webhookId)
        : [...prev, webhookId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedWebhooks.length === currentItems.length;
  const isSomeSelected = selectedWebhooks.length > 0 && selectedWebhooks.length < currentItems.length;

  // ============================================
  // CRÉATION
  // ============================================
  const handleCreateWebhook = async () => {
    if (!webhookForm.url.trim() || !webhookForm.event.trim()) {
      showAlert("Veuillez remplir tous les champs obligatoires", 'warning');
      return;
    }

    try {
      new URL(webhookForm.url);
    } catch (e) {
      showAlert("Veuillez saisir une URL valide", 'warning');
      return;
    }

    setCreateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const webhookData = {
        url: webhookForm.url.trim(),
        event: webhookForm.event,
        status: webhookForm.status
      };

      await createWebhook(token, webhookData);
      setWebhookForm({ url: '', event: '', status: 'actif' });
      setShowCreateModal(false);
      await loadWebhooks();
      showNotification('Webhook créé avec succès', 'success', 5000);
    } catch (err) {
      console.error('❌ Erreur création:', err);
      showAlert(`Erreur lors de la création: ${err.message}`, 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ============================================
  // ÉDITION
  // ============================================
  const handleEditWebhook = (webhook) => {
    setSelectedWebhook(webhook);
    setWebhookForm({
      url: webhook.url,
      event: webhook.event,
      status: webhook.status
    });
    setShowEditModal(true);
  };

  const handleUpdateWebhook = async () => {
    if (!selectedWebhook) return;
    if (!webhookForm.url.trim() || !webhookForm.event.trim()) {
      showAlert("Veuillez remplir tous les champs obligatoires", 'warning');
      return;
    }

    try {
      new URL(webhookForm.url);
    } catch (e) {
      showAlert("Veuillez saisir une URL valide", 'warning');
      return;
    }

    setUpdateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const webhookData = {
        url: webhookForm.url.trim(),
        event: webhookForm.event,
        status: webhookForm.status
      };

      await updateWebhook(token, selectedWebhook.id, webhookData);
      setShowEditModal(false);
      setSelectedWebhook(null);
      await loadWebhooks();
      showNotification('Webhook mis à jour avec succès', 'success', 5000);
    } catch (err) {
      console.error('❌ Erreur mise à jour:', err);
      showAlert(`Erreur lors de la mise à jour: ${err.message}`, 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  // ============================================
  // DÉTAILS / STATUT / TEST / SUPPRESSION
  // ============================================
  const handleShowDetails = (webhook) => {
    setSelectedWebhook(webhook);
    setShowDetailsModal(true);
  };

  const handleToggleStatus = async (webhookId, newStatus) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const webhook = webhooks.find(w => w.id === webhookId);
      if (!webhook) return;

      await updateWebhook(token, webhookId, {
        url: webhook.url,
        event: webhook.event,
        status: newStatus
      });

      await loadWebhooks();
      showNotification(
        `Webhook ${newStatus === "actif" ? "activé" : "désactivé"} avec succès`,
        'success', 4000
      );
    } catch (err) {
      console.error('❌ Erreur changement statut:', err);
      showAlert(`Erreur: ${err.message}`, 'error');
    }
  };

  const handleTestWebhook = (webhook) => {
    setSelectedWebhook(webhook);
    setTestPayload(JSON.stringify({
      event: webhook.event,
      data: {
        message: "Ceci est un test de webhook",
        timestamp: new Date().toISOString(),
        webhook_id: webhook.id
      }
    }, null, 2));
    setShowTestModal(true);
  };

  const handleConfirmTest = async () => {
    if (!selectedWebhook) return;
    setTestLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const payload = JSON.parse(testPayload);
      await testWebhook(token, selectedWebhook.id, payload);
      showNotification(`Test envoyé à: ${selectedWebhook.url}`, 'success', 4000);
      setShowTestModal(false);
    } catch (err) {
      console.error('❌ Erreur test:', err);
      showAlert(`Erreur lors du test: ${err.message}`, 'error');
    } finally {
      setTestLoading(false);
    }
  };

  const handleDeleteWebhook = (webhook) => {
    setSelectedWebhook(webhook);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedWebhook) return;
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      await deleteWebhook(token, selectedWebhook.id);
      setWebhooks(prev => prev.filter(w => w.id !== selectedWebhook.id));
      showNotification('Webhook supprimé avec succès', 'success', 5000);
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      showAlert(`Erreur lors de la suppression: ${err.message}`, 'error');
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setSelectedWebhook(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedWebhooks.length === 0) {
      showAlert("Veuillez sélectionner au moins un webhook", 'warning');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const handleConfirmBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      await Promise.all(selectedWebhooks.map(id => deleteWebhook(token, id)));
      setWebhooks(prev => prev.filter(w => !selectedWebhooks.includes(w.id)));
      showNotification(`${selectedWebhooks.length} webhook(s) supprimé(s)`, 'success', 5000);
      setSelectedWebhooks([]);
    } catch (err) {
      console.error('❌ Erreur suppression multiple:', err);
      showAlert(`Erreur: ${err.message}`, 'error');
    } finally {
      setBulkDeleteLoading(false);
      setShowBulkDeleteModal(false);
    }
  };

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedWebhooks.length > 0
      ? webhooks.filter(w => selectedWebhooks.includes(w.id))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      showAlert("Aucune donnée à exporter", 'warning', 4000);
      setExportLoading(false);
      return;
    }

    try {
      if (format === "csv") {
        const headers = ["ID", "URL", "Événement", "Statut", "Business ID", "Date Création", "Date Modification"];
        const csvContent = [
          "\uFEFF" + headers.join(";"),
          ...dataToExport.map(w => [
            w.id, w.url, w.event, w.status, w.business_id, w.created_at, w.updated_at
          ].map(v => `"${v}"`).join(";"))
        ].join("\n");
        downloadFile(csvContent, "text/csv;charset=utf-8;", `export-webhooks-${Date.now()}.csv`);
      } else if (format === "excel") {
        const ws = XLSX.utils.json_to_sheet(dataToExport.map(w => ({
          ID: w.id, URL: w.url, Événement: w.event, Statut: w.status,
          "Business ID": w.business_id, "Date Création": w.created_at, "Date Modification": w.updated_at
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Webhooks");
        XLSX.writeFile(wb, `export-webhooks-${Date.now()}.xlsx`);
      } else if (format === "json") {
        downloadFile(JSON.stringify(dataToExport, null, 2), "application/json;charset=utf-8;", `export-webhooks-${Date.now()}.json`);
      }

      showNotification(`Export ${format.toUpperCase()} réussi - ${dataToExport.length} enregistrement(s)`, 'success', 4000);
    } catch (error) {
      showAlert(`Erreur lors de l'export: ${error.message}`, 'error', 6000);
    } finally {
      setExportLoading(false);
    }
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  // ============================================
  // HELPERS UI
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="wh-sortable-header" onClick={() => handleSort(field)}>
      <span className="wh-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill wh-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  const getStatutVariant = (statut) => {
    switch (statut) {
      case 'actif': return 'success';
      case 'inactif': return 'secondary';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="wh-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {notifications.map((notification) => {
          const bgColor = getNotificationBg(notification.type);
          const icon = getNotificationIcon(notification.type);
          return (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => hideNotification(notification.id)}
              delay={8000}
              autohide
              bg={bgColor}
            >
              <Toast.Header className={`bg-${bgColor} text-white`}>
                <i className={`bi ${icon} me-2`}></i>
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' :
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">{notification.message}</Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>

      {/* ========== ALERT ========== */}
      {alertMessage.show && (
        <div className={`wh-alert wh-alert-${alertMessage.type} mb-4`}>
          <i className={`bi ${getAlertIcon(alertMessage.type)}`}></i>
          <div className="flex-grow-1">{alertMessage.message}</div>
          <button className="wh-alert-close" onClick={hideAlert}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="wh-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="wh-header-icon">
              <i className="bi bi-link-45deg"></i>
            </div>
            <div>
              <h3 className="wh-header-title mb-1">Gestion des Webhooks</h3>
              <p className="wh-header-subtitle mb-0">
                Configurez les webhooks pour recevoir des notifications en temps réel
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {selectedWebhooks.length > 0 && (
              <Button className="wh-btn-danger" onClick={handleBulkDelete}>
                <i className="bi bi-trash"></i>
                <span>Supprimer ({selectedWebhooks.length})</span>
              </Button>
            )}

            <Dropdown>
              <Dropdown.Toggle className="wh-btn-secondary" disabled={exportLoading}>
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat wh-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="wh-dropdown-menu">
                <Dropdown.Item onClick={() => handleExport('csv')}>
                  <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('json')}>
                  <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button className="wh-btn-primary" onClick={() => setShowCreateModal(true)}>
              <i className="bi bi-plus-circle"></i>
              <span>Nouveau Webhook</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="wh-alert wh-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <Button variant="outline-danger" size="sm" onClick={() => loadWebhooks(true)}>
            <i className="bi bi-arrow-clockwise me-1"></i>Réessayer
          </Button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <div className="wh-stat-card">
            <div className="wh-stat-icon-wrapper wh-stat-icon-blue">
              <i className="bi bi-link-45deg"></i>
            </div>
            <div className="wh-stat-content">
              <div className="wh-stat-label">Total webhooks</div>
              <div className="wh-stat-value">{stats.total}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={4}>
          <div className="wh-stat-card">
            <div className="wh-stat-icon-wrapper wh-stat-icon-green">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div className="wh-stat-content">
              <div className="wh-stat-label">Webhooks actifs</div>
              <div className="wh-stat-value">{stats.actifs}</div>
            </div>
          </div>
        </Col>
        <Col sm={12} lg={4}>
          <div className="wh-stat-card">
            <div className="wh-stat-icon-wrapper wh-stat-icon-gray">
              <i className="bi bi-pause-circle-fill"></i>
            </div>
            <div className="wh-stat-content">
              <div className="wh-stat-label">Webhooks inactifs</div>
              <div className="wh-stat-value">{stats.inactifs}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="wh-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={5} md={12}>
            <div className="wh-search-wrapper">
              <i className="bi bi-search wh-search-icon"></i>
              <input
                type="text"
                className="wh-search-input"
                placeholder="Rechercher par URL, événement..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="wh-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="wh-select"
              value={selectedStatutFilter}
              onChange={(e) => { setSelectedStatutFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">Tous les statuts</option>
              {statutsDisponibles.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Col>
          <Col lg={2} md={3}>
            <select
              className="wh-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </Col>
          <Col lg={2} md={3}>
            <button
              className="wh-btn-secondary w-100"
              onClick={() => loadWebhooks(true)}
              disabled={loading}
            >
              <i className={`bi ${loading ? 'bi-arrow-repeat wh-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="wh-table-card">
        {loading ? (
          <div className="wh-loading">
            <div className="wh-loading-spinner"></div>
            <div className="wh-loading-text">Chargement des webhooks...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="wh-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="wh-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="wh-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <th><i className="bi bi-link me-1"></i>URL</th>
                    <SortableHeader field="event">
                      <i className="bi bi-lightning me-1"></i>Événement
                    </SortableHeader>
                    <SortableHeader field="status">
                      <i className="bi bi-flag me-1"></i>Statut
                    </SortableHeader>
                    <SortableHeader field="created_at">
                      <i className="bi bi-calendar me-1"></i>Créé le
                    </SortableHeader>
                    <th className="text-end" style={{ width: '150px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((webhook) => {
                      const isActif = webhook.status === 'actif';
                      return (
                        <tr key={webhook.id} className="wh-row">
                          <td>
                            <div className="wh-checkbox-wrapper">
                              <input
                                type="checkbox"
                                className="wh-checkbox"
                                checked={selectedWebhooks.includes(webhook.id)}
                                onChange={() => handleSelectWebhook(webhook.id)}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="wh-url-cell">
                              <div className="wh-url-icon">
                                <i className="bi bi-globe2"></i>
                              </div>
                              <div className="wh-url-content">
                                <div className="wh-url-text" title={webhook.url}>
                                  {webhook.url}
                                </div>
                                <small className="wh-url-id">ID: {webhook.id}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="wh-event-badge">
                              <i className="bi bi-lightning-charge-fill"></i>
                              {webhook.event}
                            </span>
                          </td>
                          <td>
                            <span className={`wh-status-badge ${isActif ? 'wh-status-active' : 'wh-status-inactive'}`}>
                              <i className={`bi ${isActif ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`}></i>
                              {webhook.status}
                            </span>
                          </td>
                          <td>
                            <span className="wh-date-cell">
                              <i className="bi bi-clock-history"></i>
                              {formatDate(webhook.created_at)}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="wh-actions">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                                <button className="wh-action-btn wh-action-view" onClick={() => handleShowDetails(webhook)}>
                                  <i className="bi bi-eye"></i>
                                </button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Tester</Tooltip>}>
                                <button className="wh-action-btn wh-action-test" onClick={() => handleTestWebhook(webhook)}>
                                  <i className="bi bi-play-circle"></i>
                                </button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                <button className="wh-action-btn wh-action-delete" onClick={() => handleDeleteWebhook(webhook)}>
                                  <i className="bi bi-trash"></i>
                                </button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="wh-empty-state">
                          <div className="wh-empty-icon">
                            <i className="bi bi-link-45deg"></i>
                          </div>
                          <h5 className="wh-empty-title">Aucun webhook trouvé</h5>
                          <p className="wh-empty-text">
                            {filterText || selectedStatutFilter !== 'all'
                              ? "Aucun webhook ne correspond aux critères de recherche"
                              : "Créez votre premier webhook pour commencer"}
                          </p>
                          {(filterText || selectedStatutFilter !== 'all') ? (
                            <button className="wh-btn-empty-reset" onClick={() => { setFilterText(''); setSelectedStatutFilter('all'); }}>
                              <i className="bi bi-arrow-clockwise me-2"></i>Réinitialiser
                            </button>
                          ) : (
                            <button className="wh-btn-empty-reset" onClick={() => setShowCreateModal(true)}>
                              <i className="bi bi-plus-circle me-2"></i>Créer un webhook
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* PAGINATION */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="wh-pagination">
                <div className="wh-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedWebhooks.length > 0 && (
                    <span className="wh-pagination-selected">
                      • <strong>{selectedWebhooks.length}</strong> sélectionné{selectedWebhooks.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="wh-pagination-controls">
                  <button className="wh-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="wh-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;
                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`wh-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}
                  <button className="wh-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="wh-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== MODAL CRÉATION ========== */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered className="wh-modal">
        <div className="wh-modal-header wh-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="wh-modal-icon">
              <i className="bi bi-plus-circle-fill"></i>
            </div>
            <div>
              <h5 className="wh-modal-title mb-0">Nouveau webhook</h5>
              <small className="wh-modal-subtitle">Configurer un nouveau webhook</small>
            </div>
          </div>
          <button className="wh-modal-close" onClick={() => setShowCreateModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <Modal.Body className="wh-modal-body">
          <div className="wh-info-box mb-4">
            <i className="bi bi-info-circle-fill"></i>
            <span>Les webhooks permettent à votre application de recevoir des notifications en temps réel.</span>
          </div>
          <Row className="g-3">
            <Col md={12}>
              <div className="wh-form-group">
                <label className="wh-form-label">
                  URL de destination <span className="wh-required">*</span>
                </label>
                <input
                  type="url"
                  className="wh-input"
                  placeholder="https://votre-domaine.com/webhook"
                  value={webhookForm.url}
                  onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                />
                <div className="wh-form-hint">
                  <i className="bi bi-info-circle"></i>
                  L'URL qui recevra les notifications POST
                </div>
              </div>
            </Col>
            <Col md={12}>
              <div className="wh-form-group">
                <label className="wh-form-label">
                  Événement <span className="wh-required">*</span>
                </label>
                <select
                  className="wh-select w-100"
                  value={webhookForm.event}
                  onChange={(e) => setWebhookForm({ ...webhookForm, event: e.target.value })}
                >
                  <option value="">Sélectionnez un événement</option>
                  {evenementsDisponibles.map((evt, i) => (
                    <option key={i} value={evt}>{evt}</option>
                  ))}
                </select>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <div className="wh-modal-footer">
          <button className="wh-btn-cancel" onClick={() => setShowCreateModal(false)}>Annuler</button>
          <button
            className="wh-btn-primary"
            onClick={handleCreateWebhook}
            disabled={createLoading || !webhookForm.url.trim() || !webhookForm.event.trim()}
          >
            {createLoading ? (
              <><Spinner animation="border" size="sm" /> Création...</>
            ) : (
              <><i className="bi bi-check-lg"></i> Créer le webhook</>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL DÉTAILS ========== */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered className="wh-modal">
        <div className="wh-modal-header wh-modal-header-info">
          <div className="d-flex align-items-center gap-3">
            <div className="wh-modal-icon">
              <i className="bi bi-info-circle-fill"></i>
            </div>
            <div>
              <h5 className="wh-modal-title mb-0">Détails du webhook</h5>
              <small className="wh-modal-subtitle">Informations complètes</small>
            </div>
          </div>
          <button className="wh-modal-close" onClick={() => setShowDetailsModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <Modal.Body className="wh-modal-body">
          {selectedWebhook && (
            <>
              <div className="wh-detail-hero">
                <div className="wh-detail-avatar">
                  <i className="bi bi-link-45deg"></i>
                </div>
                <div className="wh-detail-hero-content">
                  <div className="wh-detail-hero-name">{selectedWebhook.event}</div>
                  <div className="wh-detail-hero-meta">
                    <span><i className="bi bi-hash"></i>ID: {selectedWebhook.id}</span>
                    <span><i className="bi bi-building"></i>Business: {selectedWebhook.business_id}</span>
                  </div>
                </div>
                <span className={`wh-status-badge ${selectedWebhook.status === 'actif' ? 'wh-status-active' : 'wh-status-inactive'}`}>
                  <i className={`bi ${selectedWebhook.status === 'actif' ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`}></i>
                  {selectedWebhook.status}
                </span>
              </div>

              <div className="wh-detail-grid">
                <div className="wh-detail-card">
                  <div className="wh-detail-card-icon wh-detail-card-icon-blue">
                    <i className="bi bi-calendar"></i>
                  </div>
                  <div className="wh-detail-card-content">
                    <div className="wh-detail-card-label">Créé le</div>
                    <div className="wh-detail-card-value">{formatDate(selectedWebhook.created_at)}</div>
                  </div>
                </div>
                <div className="wh-detail-card">
                  <div className="wh-detail-card-icon wh-detail-card-icon-green">
                    <i className="bi bi-arrow-repeat"></i>
                  </div>
                  <div className="wh-detail-card-content">
                    <div className="wh-detail-card-label">Modifié le</div>
                    <div className="wh-detail-card-value">{formatDate(selectedWebhook.updated_at)}</div>
                  </div>
                </div>
              </div>

              <div className="wh-detail-section">
                <div className="wh-detail-section-header">
                  <i className="bi bi-globe2"></i>
                  <span>URL de destination</span>
                </div>
                <div className="wh-detail-section-body">
                  <code className="wh-url-code">{selectedWebhook.url}</code>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <div className="wh-modal-footer">
          <button className="wh-btn-cancel" onClick={() => setShowDetailsModal(false)}>Fermer</button>
        </div>
      </Modal>

      {/* ========== MODAL TEST ========== */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)} size="lg" centered className="wh-modal">
        <div className="wh-modal-header wh-modal-header-warning">
          <div className="d-flex align-items-center gap-3">
            <div className="wh-modal-icon">
              <i className="bi bi-play-circle-fill"></i>
            </div>
            <div>
              <h5 className="wh-modal-title mb-0">Tester le webhook</h5>
              <small className="wh-modal-subtitle">Envoyer une requête test</small>
            </div>
          </div>
          <button className="wh-modal-close" onClick={() => setShowTestModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <Modal.Body className="wh-modal-body">
          {selectedWebhook && (
            <>
              <div className="wh-info-box mb-4">
                <i className="bi bi-info-circle-fill"></i>
                <span>Envoyez une requête test pour vérifier que votre webhook fonctionne.</span>
              </div>
              <div className="wh-form-group mb-3">
                <label className="wh-form-label">URL de destination</label>
                <input type="text" className="wh-input" value={selectedWebhook.url} readOnly />
              </div>
              <div className="wh-form-group">
                <label className="wh-form-label">Payload de test</label>
                <textarea
                  className="wh-textarea"
                  rows={8}
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <div className="wh-modal-footer">
          <button className="wh-btn-cancel" onClick={() => setShowTestModal(false)}>Annuler</button>
          <button className="wh-btn-warning" onClick={handleConfirmTest} disabled={testLoading}>
            {testLoading ? (
              <><Spinner animation="border" size="sm" /> Test...</>
            ) : (
              <><i className="bi bi-play-fill"></i> Envoyer le test</>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL SUPPRESSION ========== */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="wh-modal">
        <Modal.Body className="wh-delete-body">
          <div className="wh-delete-icon-wrapper">
            <div className="wh-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="wh-delete-title">Confirmer la suppression</h4>
          <p className="wh-delete-text">Êtes-vous sûr de vouloir supprimer ce webhook ?</p>
          {selectedWebhook && (
            <div className="wh-delete-info">
              <div className="wh-delete-url">{selectedWebhook.url}</div>
              <div className="wh-delete-meta">
                <span className="wh-event-badge">{selectedWebhook.event}</span>
              </div>
            </div>
          )}
          <div className="wh-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>
          <div className="wh-delete-actions">
            <button className="wh-btn-cancel wh-btn-flex" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
              <i className="bi bi-x-lg"></i> Annuler
            </button>
            <button className="wh-btn-danger wh-btn-flex" onClick={handleConfirmDelete} disabled={deleteLoading}>
              {deleteLoading ? (
                <><Spinner animation="border" size="sm" /> Suppression...</>
              ) : (
                <><i className="bi bi-trash"></i> Supprimer</>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ========== MODAL SUPPRESSION MULTIPLE ========== */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered className="wh-modal">
        <Modal.Body className="wh-delete-body">
          <div className="wh-delete-icon-wrapper">
            <div className="wh-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="wh-delete-title">Suppression multiple</h4>
          <p className="wh-delete-text">
            Vous êtes sur le point de supprimer <strong>{selectedWebhooks.length} webhook(s)</strong>.
          </p>
          <div className="wh-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>
          <div className="wh-delete-actions">
            <button className="wh-btn-cancel wh-btn-flex" onClick={() => setShowBulkDeleteModal(false)} disabled={bulkDeleteLoading}>
              <i className="bi bi-x-lg"></i> Annuler
            </button>
            <button className="wh-btn-danger wh-btn-flex" onClick={handleConfirmBulkDelete} disabled={bulkDeleteLoading}>
              {bulkDeleteLoading ? (
                <><Spinner animation="border" size="sm" /> Suppression...</>
              ) : (
                <><i className="bi bi-trash"></i> Supprimer ({selectedWebhooks.length})</>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .wh-page { background: #f8fafc; min-height: 100vh; }
        .wh-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

        /* ========== HEADER ========== */
        .wh-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .wh-header-icon {
          width: 56px; height: 56px; border-radius: 14px;
          background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 26px; box-shadow: 0 8px 20px -6px rgba(6, 182, 212, 0.5);
        }
        .wh-header-title {
          font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em;
        }
        .wh-header-subtitle { color: #64748b; font-size: 0.875rem; }

        /* ========== BUTTONS ========== */
        .wh-btn-primary, .wh-btn-secondary, .wh-btn-cancel, .wh-btn-danger, .wh-btn-warning {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 20px; border-radius: 10px; font-weight: 600; font-size: 0.875rem;
          border: none; transition: all 0.2s; cursor: pointer; text-decoration: none;
        }
        .wh-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white;
        }
        .wh-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .wh-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .wh-btn-secondary {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .wh-btn-secondary:hover:not(:disabled) {
          background: #f8fafc; border-color: #cbd5e1; color: #0f172a;
        }
        .wh-btn-secondary:disabled { opacity: 0.6; }
        .wh-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: white;
        }
        .wh-btn-danger:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .wh-btn-warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white;
        }
        .wh-btn-warning:hover:not(:disabled) {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          transform: translateY(-1px);
        }
        .wh-btn-cancel {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .wh-btn-cancel:hover { background: #f1f5f9; }
        .wh-btn-flex { flex: 1; }
        .wh-dropdown-menu {
          border: 1px solid #e2e8f0; border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1); padding: 6px;
        }
        .wh-dropdown-menu .dropdown-item {
          border-radius: 6px; padding: 8px 12px; font-size: 0.85rem; display: flex; align-items: center;
        }
        .wh-dropdown-menu .dropdown-item:hover { background: #f1f5f9; }

        /* ========== ALERTS ========== */
        .wh-alert {
          display: flex; align-items: center; gap: 12px; padding: 14px 18px;
          border-radius: 12px; font-size: 0.85rem; font-weight: 500;
        }
        .wh-alert > i { font-size: 1.3rem; flex-shrink: 0; }
        .wh-alert-success { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); color: #065f46; border-left: 4px solid #10b981; }
        .wh-alert-danger { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); color: #991b1b; border-left: 4px solid #ef4444; }
        .wh-alert-warning { background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); color: #92400e; border-left: 4px solid #f59e0b; }
        .wh-alert-info { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); color: #1e40af; border-left: 4px solid #3b82f6; }
        .wh-alert-close {
          background: none; border: none; color: inherit; opacity: 0.6; cursor: pointer;
          padding: 4px 8px; border-radius: 6px; font-size: 1rem;
        }
        .wh-alert-close:hover { opacity: 1; background: rgba(0,0,0,0.05); }

        /* ========== STATS ========== */
        .wh-stat-card {
          display: flex; align-items: center; gap: 14px; background: white;
          border-radius: 14px; padding: 18px 20px; border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .wh-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08); }
        .wh-stat-icon-wrapper {
          width: 46px; height: 46px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
        }
        .wh-stat-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .wh-stat-icon-green { background: #d1fae5; color: #10b981; }
        .wh-stat-icon-gray  { background: #f1f5f9; color: #64748b; }
        .wh-stat-content { min-width: 0; flex-grow: 1; }
        .wh-stat-label {
          font-size: 0.72rem; color: #64748b; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px;
        }
        .wh-stat-value {
          font-size: 1.5rem; font-weight: 700; color: #0f172a;
          letter-spacing: -0.02em; line-height: 1;
        }

        /* ========== FILTRES ========== */
        .wh-filters {
          background: white; border-radius: 14px; padding: 16px 20px; border: 1px solid #e2e8f0;
        }
        .wh-search-wrapper { position: relative; display: flex; align-items: center; }
        .wh-search-icon {
          position: absolute; left: 14px; color: #94a3b8; font-size: 1.05rem; pointer-events: none;
        }
        .wh-search-input {
          width: 100%; padding: 11px 40px 11px 42px; border: 1px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; background: #f8fafc;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .wh-search-input:focus {
          background: white; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .wh-search-clear {
          position: absolute; right: 8px; background: none; border: none; color: #94a3b8;
          font-size: 1rem; padding: 6px 10px; cursor: pointer; border-radius: 6px; transition: all 0.15s;
        }
        .wh-search-clear:hover { background: #fee2e2; color: #ef4444; }
        .wh-select {
          width: 100%; padding: 11px 14px; border: 1px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; font-weight: 500;
          background: #f8fafc; color: #334155; outline: none; cursor: pointer; font-family: inherit;
        }
        .wh-select:focus {
          background: white; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* ========== TABLE ========== */
        .wh-table-card {
          background: white; border-radius: 16px; border: 1px solid #e2e8f0;
          overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .wh-table { font-size: 0.875rem; }
        .wh-table thead th {
          background: #f8fafc; color: #475569; font-weight: 700; font-size: 0.7rem;
          text-transform: uppercase; letter-spacing: 0.05em; padding: 16px;
          border-bottom: 1px solid #e2e8f0; border-top: none; white-space: nowrap;
        }
        .wh-table tbody td {
          padding: 16px; vertical-align: middle; border-bottom: 1px solid #f1f5f9;
        }
        .wh-row { transition: background-color 0.15s; }
        .wh-row:hover { background-color: #f8fafc; }
        .wh-sortable-header { cursor: pointer; user-select: none; }
        .wh-sortable-header:hover { color: #3b82f6; }
        .wh-sortable-content { display: inline-flex; align-items: center; gap: 6px; }
        .wh-sort-icon { color: #3b82f6; font-size: 0.75rem; }

        .wh-checkbox-wrapper { display: flex; align-items: center; justify-content: center; }
        .wh-checkbox {
          width: 18px; height: 18px; border-radius: 5px; border: 1.5px solid #cbd5e1;
          cursor: pointer; accent-color: #3b82f6;
        }

        /* URL cell */
        .wh-url-cell { display: flex; align-items: center; gap: 12px; }
        .wh-url-icon {
          width: 38px; height: 38px; border-radius: 10px;
          background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 16px; flex-shrink: 0;
        }
        .wh-url-content { min-width: 0; flex-grow: 1; }
        .wh-url-text {
          font-weight: 600; color: #0f172a; font-size: 0.82rem;
          max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .wh-url-id { color: #94a3b8; font-size: 0.7rem; font-family: 'SF Mono', monospace; }

        /* Event badge */
        .wh-event-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 6px;
          background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
          color: #7c3aed; font-size: 0.72rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.03em;
        }

        /* Status badge */
        .wh-status-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 11px; border-radius: 6px;
          font-weight: 700; font-size: 0.72rem; white-space: nowrap;
        }
        .wh-status-active { background: #d1fae5; color: #059669; }
        .wh-status-inactive { background: #f1f5f9; color: #64748b; }

        /* Date cell */
        .wh-date-cell {
          display: inline-flex; align-items: center; gap: 6px;
          color: #64748b; font-size: 0.8rem; font-variant-numeric: tabular-nums;
        }
        .wh-date-cell i { color: #94a3b8; }

        /* Actions */
        .wh-actions { display: inline-flex; gap: 6px; justify-content: flex-end; }
        .wh-action-btn {
          width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e2e8f0;
          background: white; color: #64748b; display: inline-flex;
          align-items: center; justify-content: center; cursor: pointer;
          transition: all 0.15s; font-size: 0.9rem;
        }
        .wh-action-view:hover {
          background: #06b6d4; border-color: #06b6d4; color: white;
          transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
        }
        .wh-action-test:hover {
          background: #f59e0b; border-color: #f59e0b; color: white;
          transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(245, 158, 11, 0.4);
        }
        .wh-action-delete:hover {
          background: #ef4444; border-color: #ef4444; color: white;
          transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .wh-loading { padding: 80px 20px; text-align: center; }
        .wh-loading-spinner {
          width: 48px; height: 48px; border: 4px solid #e2e8f0;
          border-top-color: #3b82f6; border-radius: 50%;
          animation: wh-spin 0.8s linear infinite; margin: 0 auto 16px;
        }
        .wh-loading-text { color: #64748b; font-size: 0.875rem; font-weight: 500; }
        .wh-empty-state { padding: 60px 20px; text-align: center; }
        .wh-empty-icon {
          width: 80px; height: 80px; border-radius: 50%; background: #f1f5f9; color: #94a3b8;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 36px; margin-bottom: 20px;
        }
        .wh-empty-title { color: #334155; font-weight: 700; margin-bottom: 8px; }
        .wh-empty-text { color: #94a3b8; margin-bottom: 20px; }
        .wh-btn-empty-reset {
          display: inline-flex; align-items: center; background: white; color: #3b82f6;
          border: 1px solid #bfdbfe; border-radius: 8px; font-weight: 600;
          padding: 8px 16px; cursor: pointer; transition: all 0.2s;
        }
        .wh-btn-empty-reset:hover { background: #3b82f6; color: white; border-color: #3b82f6; }

        /* ========== PAGINATION ========== */
        .wh-pagination {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 24px; background: #f8fafc; border-top: 1px solid #e2e8f0;
          flex-wrap: wrap; gap: 12px;
        }
        .wh-pagination-info {
          font-size: 0.8rem; color: #64748b; display: flex; align-items: center; gap: 6px;
        }
        .wh-pagination-info strong { color: #0f172a; }
        .wh-pagination-selected { color: #3b82f6; margin-left: 8px; }
        .wh-pagination-controls { display: flex; align-items: center; gap: 6px; }
        .wh-page-btn {
          min-width: 34px; height: 34px; padding: 0 10px; border-radius: 8px;
          border: 1px solid #e2e8f0; background: white; color: #475569;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s; font-size: 0.8rem; font-weight: 600;
        }
        .wh-page-btn:hover:not(:disabled) { background: #3b82f6; color: white; border-color: #3b82f6; }
        .wh-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .wh-page-btn.active { background: #3b82f6; color: white; border-color: #3b82f6; }

        /* ========== MODAL ========== */
        .wh-modal .modal-content { border: none; border-radius: 16px; overflow: hidden; }
        .wh-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; color: white;
        }
        .wh-modal-header-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .wh-modal-header-info { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }
        .wh-modal-header-warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
        .wh-modal-icon {
          width: 44px; height: 44px; border-radius: 10px; background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center; font-size: 20px;
        }
        .wh-modal-title { font-weight: 700; letter-spacing: -0.01em; margin: 0; }
        .wh-modal-subtitle { color: rgba(255,255,255,0.85); font-size: 0.8rem; }
        .wh-modal-close {
          background: rgba(255,255,255,0.1); border: none; color: white;
          width: 36px; height: 36px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 1rem;
        }
        .wh-modal-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
        .wh-modal-body {
          padding: 24px; background: #f8fafc; max-height: 65vh; overflow-y: auto;
        }
        .wh-modal-footer {
          padding: 16px 24px; background: white; border-top: 1px solid #e2e8f0;
          display: flex; justify-content: flex-end; gap: 10px;
        }

        /* Form */
        .wh-form-group { margin-bottom: 4px; }
        .wh-form-label {
          display: block; font-weight: 700; color: #0f172a; font-size: 0.82rem; margin-bottom: 8px;
        }
        .wh-required { color: #ef4444; }
        .wh-input {
          width: 100%; padding: 11px 14px; border: 2px solid #e2e8f0; border-radius: 10px;
          font-size: 0.875rem; color: #0f172a; background: white;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .wh-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
        .wh-textarea {
          width: 100%; padding: 11px 14px; border: 2px solid #e2e8f0; border-radius: 10px;
          font-size: 0.8rem; color: #0f172a; background: white; transition: all 0.2s;
          outline: none; font-family: 'SF Mono', Menlo, Monaco, monospace; resize: vertical;
        }
        .wh-textarea:focus { border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
        .wh-form-hint {
          margin-top: 6px; font-size: 0.72rem; color: #94a3b8;
          display: flex; align-items: center; gap: 4px;
        }
        .wh-form-hint i { color: #3b82f6; }

        /* Info box */
        .wh-info-box {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 12px 16px; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 10px; border-left: 4px solid #3b82f6;
          color: #1e40af; font-size: 0.82rem;
        }
        .wh-info-box i { font-size: 1.1rem; flex-shrink: 0; margin-top: 1px; }

        /* Detail Hero */
        .wh-detail-hero {
          display: flex; align-items: center; gap: 16px; padding: 20px;
          background: white; border-radius: 14px; border: 1px solid #e2e8f0;
          margin-bottom: 20px; flex-wrap: wrap;
        }
        .wh-detail-avatar {
          width: 64px; height: 64px; border-radius: 16px;
          background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 26px; flex-shrink: 0;
          box-shadow: 0 8px 16px -4px rgba(6, 182, 212, 0.3);
        }
        .wh-detail-hero-content { flex-grow: 1; min-width: 0; }
        .wh-detail-hero-name {
          font-weight: 700; color: #0f172a; font-size: 1.15rem; margin-bottom: 6px;
          text-transform: uppercase; letter-spacing: 0.02em;
        }
        .wh-detail-hero-meta {
          display: flex; flex-wrap: wrap; gap: 14px; font-size: 0.82rem; color: #64748b;
        }
        .wh-detail-hero-meta span { display: inline-flex; align-items: center; gap: 5px; }
        .wh-detail-hero-meta i { color: #94a3b8; }

        /* Detail Grid */
        .wh-detail-grid {
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 20px;
        }
        .wh-detail-card {
          display: flex; align-items: center; gap: 14px; padding: 16px;
          background: white; border-radius: 12px; border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }
        .wh-detail-card:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -6px rgba(0, 0, 0, 0.08); }
        .wh-detail-card-icon {
          width: 42px; height: 42px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
        }
        .wh-detail-card-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .wh-detail-card-icon-green  { background: #d1fae5; color: #059669; }
        .wh-detail-card-content { flex-grow: 1; min-width: 0; }
        .wh-detail-card-label {
          font-size: 0.68rem; color: #94a3b8; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .wh-detail-card-value { font-weight: 700; color: #0f172a; font-size: 0.9rem; }

        /* Detail Section */
        .wh-detail-section {
          background: white; border-radius: 12px; border: 1px solid #e2e8f0; overflow: hidden;
        }
        .wh-detail-section-header {
          display: flex; align-items: center; gap: 8px; padding: 14px 18px;
          background: #f8fafc; border-bottom: 1px solid #e2e8f0;
          font-weight: 700; color: #0f172a; font-size: 0.85rem;
        }
        .wh-detail-section-header i { color: #3b82f6; font-size: 1rem; }
        .wh-detail-section-body { padding: 16px 18px; }
        .wh-url-code {
          display: block; padding: 12px 16px; background: #f8fafc;
          border: 1px solid #e2e8f0; border-radius: 8px;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          font-size: 0.8rem; color: #1e40af; word-break: break-all;
        }

        /* Delete modal */
        .wh-delete-body { padding: 32px 24px 24px; text-align: center; }
        .wh-delete-icon-wrapper { margin-bottom: 20px; }
        .wh-delete-icon {
          width: 80px; height: 80px; border-radius: 50%;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626; display: inline-flex; align-items: center; justify-content: center;
          font-size: 36px; box-shadow: 0 12px 24px -8px rgba(220, 38, 38, 0.3);
        }
        .wh-delete-title { font-weight: 700; color: #0f172a; margin-bottom: 12px; font-size: 1.25rem; }
        .wh-delete-text { color: #64748b; margin-bottom: 20px; font-size: 0.875rem; }
        .wh-delete-info {
          background: #f8fafc; border-radius: 12px; padding: 16px 20px;
          margin-bottom: 16px; border: 1px solid #e2e8f0; text-align: left;
        }
        .wh-delete-url {
          font-family: 'SF Mono', monospace; font-size: 0.8rem;
          color: #1e40af; margin-bottom: 8px; word-break: break-all;
        }
        .wh-delete-meta { display: flex; gap: 8px; flex-wrap: wrap; }
        .wh-delete-warning {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; background: #fef2f2; color: #dc2626;
          border-radius: 8px; font-size: 0.78rem; font-weight: 700; margin-bottom: 20px;
        }
        .wh-delete-actions { display: flex; gap: 10px; }

        /* ========== ANIMATIONS ========== */
        .wh-spin { animation: wh-spin 1s linear infinite; display: inline-block; }
        @keyframes wh-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .wh-detail-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .wh-header { padding: 20px; }
          .wh-header-title { font-size: 1.2rem; }
          .wh-pagination { flex-direction: column; }
          .wh-stat-value { font-size: 1.25rem; }
          .wh-modal-body { padding: 16px; }
          .wh-delete-actions { flex-direction: column; }
          .wh-detail-hero { padding: 16px; }
          .wh-detail-avatar { width: 52px; height: 52px; font-size: 20px; }
          .wh-url-text { max-width: 180px; }
        }
      `}</style>
    </div>
  );
}